import { useDispatch, useSelector } from "react-redux"
import AllActivitiesMap from "../components/AllActivitiesMap"
import PageContainer from "../components/PageContainer"
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { getUserMapActivities } from "../redux/userMapActivities"
import Pool from "../components/poolBar/Pool"
import { useTranslation } from "react-i18next"
import error404 from '../static/images/404.jpg';
import { resetBacklink } from "../redux/backlinkSlice"
import useWindowWidth from "../hooks/useWindowWidth"
import { Helmet } from 'react-helmet-async';

// Mobile breakpoint Tailwind md
const MD_BREAKPOINT = 768;
// Altezza della BottomBar fissa (BaseBar.jsx: py-4 + h-12 buttons → 32 + 48 = 80px)
const BOTTOM_BAR_HEIGHT_PX = 80;
// Min altezza ragionevole per la mappa, fallback se il calcolo fallisce
const MIN_MAP_HEIGHT_PX = 300;

const MapPage = () => {
    const { t, i18n } = useTranslation();
    const { isLoading, error, data, activitiesAmount } = useSelector(state => state.userMapActivities)
    const filters = useSelector(state => state.filters);
    const { link } = useSelector(state => state.backlink);
    const windowWidth = useWindowWidth();
    const isMobile = windowWidth < MD_BREAKPOINT;

    // Eseguo la chiamata per ricere tutte le attività al montaggio del componente
    const dispatch = useDispatch();
    // Questo è lo useEffect che rieffettua le chiamate ogni volta che filters cambia
    useEffect(() => {
        // Impedisco la chiamata se l'utente torna da una pagina dettaglio
        if (link === "/activity-details") {
            dispatch(resetBacklink());
            return;
        }
        dispatch(getUserMapActivities({ filters, lang: i18n.language }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, filters, i18n.language])

    // Archivio tutte le coordinate delle attività ricevute
    const [markersData, setMarkersData] = useState(null)
    useEffect(() => {
        if (data) {
            let allActivities = [];
            data.forEach((element) => {
                let newObject = {};
                // Aggiungere qui dati da passare ai markers delle attività
                newObject.id = element._id;
                newObject.title = element.title;
                newObject.cover = element.coverImageUrl;
                newObject.lat = element.coordinates.lat;
                newObject.lng = element.coordinates.lng;
                newObject.activityType = element.activityType[0];
                newObject.category = element.category[0];
                newObject.difficultyLevel = element.difficultyLevel || [];
                newObject.roadLink = `https://www.google.com/maps/dir/?api=1&destination=${element.coordinates.lat},${element.coordinates.lng}`
                allActivities.push(newObject);
            })
            setMarkersData(allActivities);
        }
    }, [data])

    // Mobile: calcola dinamicamente l'altezza della mappa per riempire dal suo top fino a (viewport - BottomBar).
    // Robusto rispetto a Navbar, padding, e altezza dinamica del Pool (filtri).
    const mapAreaRef = useRef(null);
    const [mobileMapHeight, setMobileMapHeight] = useState(null);
    useLayoutEffect(() => {
        const computeHeight = () => {
            if (typeof window === 'undefined') return;
            if (window.innerWidth >= MD_BREAKPOINT) {
                setMobileMapHeight(null); // desktop: layout originario invariato
                return;
            }
            const node = mapAreaRef.current;
            if (!node) return;
            const rect = node.getBoundingClientRect();
            const available = window.innerHeight - rect.top - BOTTOM_BAR_HEIGHT_PX;
            setMobileMapHeight(Math.max(MIN_MAP_HEIGHT_PX, available));
        };
        computeHeight();

        // ResizeObserver su document.body: intercetta cambi di layout indotti dal Pool
        // (espansione/collasso filtri) che window.resize non riesce a vedere.
        let observer = null;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => computeHeight());
            observer.observe(document.body);
        }

        window.addEventListener('resize', computeHeight);
        window.addEventListener('orientationchange', computeHeight);
        return () => {
            if (observer) observer.disconnect();
            window.removeEventListener('resize', computeHeight);
            window.removeEventListener('orientationchange', computeHeight);
        };
    }, [data, isLoading, error, markersData, isMobile]);

    // --- MOBILE: layout immersivo, mappa full-screen con Pool floating come card ---
    if (isMobile) {
        return (
            <>
            <Helmet>
                <title>Mappa attività - TicinoWild</title>
                <meta name="description" content="Esplora le attività outdoor del Ticino sulla mappa interattiva. Trekking, sport acquatici, punti panoramici: scopri tutto vicino a te." />
                <link rel="canonical" href="https://ticinowild.ch/map-page" />
                <meta property="og:title" content="Mappa attività - TicinoWild" />
                <meta property="og:description" content="Esplora le attività outdoor del Ticino sulla mappa interattiva. Trekking, sport acquatici, punti panoramici: scopri tutto vicino a te." />
                <meta property="og:url" content="https://ticinowild.ch/map-page" />
                <meta name="twitter:title" content="Mappa attività - TicinoWild" />
                <meta name="twitter:description" content="Esplora le attività outdoor del Ticino sulla mappa interattiva. Trekking, sport acquatici, punti panoramici: scopri tutto vicino a te." />
            </Helmet>
            <div
                ref={mapAreaRef}
                className="w-full relative"
                style={mobileMapHeight ? { height: `${mobileMapHeight}px` } : undefined}
            >
                {/* Loading thickness */}
                {isLoading && (
                    <div className="absolute inset-0 min-h-[100px]" />
                )}

                {/* Map layer */}
                {!isLoading && !error && markersData && (
                    <div className="absolute inset-0">
                        <AllActivitiesMap
                            markersData={markersData}
                            fillContainer
                            activitiesCount={activitiesAmount}
                        />
                    </div>
                )}

                {/* 404 nessuna attività trovata */}
                {data && data.length === 0 && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center bg-[#f7f7f8]">
                        <div className="text-6xl text-[#225424] font-signature mb-4">{t('AllActivities.comingSoonTitle')}</div>
                        <div className="text-[#b05704] text-base">{t('AllActivities.comingSoonSubtitle')}</div>
                    </div>
                )}

                {/* Error specifico backend */}
                {error && error.message === "Nessuna attività trovata" && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[#f7f7f8]">
                        <h3 className="text-[#225424]">{t('AllActivities.noDataFoundError')}</h3>
                    </div>
                )}

                {/* Pool floating card sopra la mappa.
                    z-10: deve stare sopra la mappa (sibling più avanti nel DOM),
                    sotto LanguageSelector (z-30 in body) e sotto AllFilters modale (z-50 via portal). */}
                <div className="absolute top-2 left-2 right-2 z-10">
                    <div className="rounded-2xl shadow-lg bg-white overflow-hidden">
                        <Pool />
                    </div>
                </div>
            </div>
            </>
        );
    }

    // --- DESKTOP: layout originario invariato ---
    return (
        <PageContainer items="center">
            <Helmet>
                <title>Mappa attività - TicinoWild</title>
                <meta name="description" content="Esplora le attività outdoor del Ticino sulla mappa interattiva. Trekking, sport acquatici, punti panoramici: scopri tutto vicino a te." />
                <link rel="canonical" href="https://ticinowild.ch/map-page" />
                <meta property="og:title" content="Mappa attività - TicinoWild" />
                <meta property="og:description" content="Esplora le attività outdoor del Ticino sulla mappa interattiva. Trekking, sport acquatici, punti panoramici: scopri tutto vicino a te." />
                <meta property="og:url" content="https://ticinowild.ch/map-page" />
                <meta name="twitter:title" content="Mappa attività - TicinoWild" />
                <meta name="twitter:description" content="Esplora le attività outdoor del Ticino sulla mappa interattiva. Trekking, sport acquatici, punti panoramici: scopri tutto vicino a te." />
            </Helmet>

            <Pool />

            {
                // Thickness durante il loading
                isLoading &&
                <div className="min-h-[100px] bg-red-300">
                </div>
            }

            {!isLoading && !error && markersData && (
                <div className="w-full flex justify-center">
                    <AllActivitiesMap
                        markersData={markersData}
                        activitiesCount={activitiesAmount}
                    />
                </div>
            )}

            {
                /* 404 nessun elemento trovato */
                data && data.length === 0 &&
                <div className="mt-4 mx-16 flex flex-col xl:flex-row gap-8 justify-center items-center">
                    {/* Copy */}
                    <div className="flex flex-col items-center gap-4 text-center">
                        <div className="text-8xl text-[#225424] font-signature mb-6">{t('AllActivities.comingSoonTitle')}</div>
                        <div className="text-[#b05704] text-xl">{t('AllActivities.comingSoonSubtitle')}</div>
                    </div>

                    {/* Foto */}
                    <div className="rounded-xl overflow-hidden">
                        <img src={error404} alt="404" className="md:max-w-[800px]" />
                    </div>
                </div>

            }
            {
                error && error.message === "Nessuna attività trovata" &&
                <h3 className="mt-4 text-[#225424]">{t('AllActivities.noDataFoundError')}</h3>
            }
        </PageContainer>
    )
}

export default MapPage
